import { DataEmFormatoISO, EmpregadoId, ID } from "@/tipos";


export type Decimal = number;
export type NomePessoa = string;
export type Texto = string;

/** Ex.: "08:30" / "18:05" (o backend envia string; o formato exato pode variar) */
export type HoraHHMM = string;

/** Ex.: "#FFAA00" */
export type CorHex = string;

/** Strings com valores/durações que não possuem especificação formal aqui. */
export type ValorTempoOuSaldo = string;

/**
 * Momento da jornada ao qual a marcação se refere.
 *
 * No backend isso é o enum `MomentoDaJornada` e é serializado como string no JSON com os valores:
 * - `"Entrada"`
 * - `"Pausa"`
 * - `"Retorno Pausa"`
 * - `"Saída"`
 */
export type MomentoDaJornada = "Entrada" | "Pausa" | "Retorno Pausa" | "Saída";

/**
 * "Kind"/origem do registro do ponto no payload do Dia da Folha.
 *
 * Valores observados neste contexto:
 * - `"timeLog"`: marcação real (ver `DiaDaFolha._formatar_marcacao_para_api`)
 * - `"fake"`: correção/placeholder (ver `CorrecaoDaFolha.tipo_do_ponto`)
 */
export type TipoDoPonto = "timeLog" | "fake";

/**
 * Tipo de medição de tempo em justificativas/dispensas.
 *
 * Valores esperados no ecossistema:
 * - `"interval"`: medido por intervalo (usa `startTime`/`endTime`)
 * - `"duration"`: medido por duração (usa `duration`, sem `startTime`/`endTime`)
 */
export type TimeMeasurementType = "interval" | "duration";

/**
 * Estado do travamento da folha (relacionado ao `LockRequest.state`).
 *
 * Esse campo no `DiaDaFolhaResponse` é persistido/propagado como string (pode vir `null`)
 * e tende a refletir o `state` do lock request no Mongo.
 *
 * Valores conhecidos (do modelo de travamento):
 * - `"wait_start_confirmations"`
 * - `"no_confirmation"`
 * - `"rh_confirmed"`
 * - `"employee_confirmed"`
 * - `"rh_denied"`
 * - `"employee_denied"`
 * - `"all_confirmed"`
 * - `"employee_unilateral"`
 * - `"rh_unilateral"`
 */
export type EstadoTravamento =
  | "wait_start_confirmations"
  | "no_confirmation"
  | "rh_confirmed"
  | "employee_confirmed"
  | "rh_denied"
  | "employee_denied"
  | "all_confirmed"
  | "employee_unilateral"
  | "rh_unilateral";

export interface JornadaSobreaviso {
  id: ID;
  cor: CorHex;
  tipo: string;
  inicio: HoraHHMM;
  fim: HoraHHMM;
}

export interface Apontamento {
  nome_padrao: Texto;
  nome_customizado: Texto;
  nome_customizado_abreviado: Texto;
  exibir_pdf: boolean;
  exibir_folha: boolean;
  valor: ValorTempoOuSaldo;
}

export interface AlteracaoAbono {
  nome_apontamento: Texto;
  valor: ValorTempoOuSaldo;
  silencioso: boolean;
}

export interface Abono {
  id: ID;
  aprovado: boolean;
  aprovado_manualmente: boolean;
  alteracoes: ReadonlyArray<AlteracaoAbono>;
}

export interface SaldoDiario {
  id: ID;
  ativo: boolean;
  descricao: Texto;
  eh_baixa: boolean;
  eh_banco_manual: boolean;
  eh_compensacao: boolean;
  esta_compensado: boolean;
  eh_evento: boolean;
  esta_aprovado: boolean;
  valor: ValorTempoOuSaldo;
  esta_vencido: boolean;
  eh_baixa_parcial: boolean;
  eh_gatilho: boolean;
}

export interface Marcacao {
  id: ID;
  correcao_id?: ID;
  marcacao_id?: ID;
  fake_id?: ID;
  hora_marcacao: HoraHHMM;
  momento_da_jornada: MomentoDaJornada;
  tipo_do_ponto: TipoDoPonto;
  eh_autocompletar: boolean;
  justificativa: Texto;
  processado: boolean;
  criado_por_correcao_automatica: boolean;
  anulado: boolean;
  ponto: Texto;
  criado_por_ia: boolean;
  ponto_manual_sobrescrito: boolean;
}

export interface Jornada {
  nome_da_jornada: Texto;
  cor_da_jornada: CorHex;
  inicio_jornada: HoraHHMM;
  inicio_pausa: HoraHHMM;
  fim_pausa: HoraHHMM;
  fim_jornada: HoraHHMM;
  jornadas_de_sobreaviso: ReadonlyArray<JornadaSobreaviso> | null;
}

export interface JornadaDiaria {
  id_jornada_diaria: ID;
  eh_feriado: boolean;
  eh_ferias: boolean;
  eh_afastamento: boolean;
  eh_dia_de_trabalho?: boolean;
  falta: boolean;
  trabalho_remoto: boolean;
  alterada_manualmente: boolean;
}

export interface Linha {
  id_linha: ID | null;
  pontos: ReadonlyArray<ReadonlyArray<Marcacao>>;
  desconsiderar_horas_extras_de_feriado: boolean | null;
  desconsiderar_pausa: boolean | null;
  observacao_da_linha: Texto | null;
}

export interface JustificativaMotivo {
  id: ID;
  description: Texto;
}

export type LoseDsrMotivo = string;
export type LoseDsr = boolean | LoseDsrMotivo;

export interface Justificativa {
  id: ID;
  code: string;
  typeId: ID;
  employee: EmpregadoId;
  comp_man: ID;
  company: ID;
  reason: JustificativaMotivo;
  allDay: boolean;
  startTime: HoraHHMM;
  endTime: HoraHHMM;
  isFalta: boolean;
  isDsr: boolean;
  ignorarFolga: boolean | null;
  date: DataEmFormatoISO;
  kind: string;
  specifiedLunchDur: Decimal | null;
  duration: Decimal | null;
  travelTime: Decimal | null;
  timeMeasurementType: TimeMeasurementType;
  rep_journey: ID | null;
  loseDsr: LoseDsr;
  hoursAreWork: boolean;
  hoursAreNothing: boolean;
  dispensasExtras?: ReadonlyArray<Justificativa> | null;
}

export interface Nota {
  id: ID;
  /** `datetime` no backend (serializa em string ISO no JSON). */
  dia: DataEmFormatoISO;
  texto: Texto;
  /** `datetime | None` no backend */
  ultima_atualizacao: DataEmFormatoISO | null;
  dono: EmpregadoId;
  empregado: EmpregadoId;
}

export interface DiaDaFolhaTravamento {
  folha_travada: boolean;
  estado_travamento: EstadoTravamento | null;
  horario_travamento: HoraHHMM | null;
}

export interface DiaDaFolha {
  dia: DataEmFormatoISO;
  empregado_id: EmpregadoId;
  nome_empregado: NomePessoa;
  apontamentos: ReadonlyArray<Apontamento>;
  jornada: Jornada;
  jornada_diaria: JornadaDiaria;
  linha: Linha;
  nome_da_regra_de_calculo_usada: string;
  saldos_diarios: ReadonlyArray<SaldoDiario>;
  justificativa: Justificativa | null;
  travamento: DiaDaFolhaTravamento;
  precisa_recalcular: boolean;
  /**
   * A jornada do dia foi marcada para ser regerada a partir do criador de
   * jornadas (`deve_ser_regerada` no back). Não confundir com
   * `precisa_recalcular`: lá o cálculo do dia está sujo, aqui a própria jornada
   * está errada. As duas pendências têm rotas diferentes.
   */
  jornada_precisa_ser_regerada?: boolean;
  notas: ReadonlyArray<Nota> | null;
  dia_verificado?: boolean;
  abono: Abono | null;
}

export interface DiaSemDiaDaFolha {
  empregado_id: EmpregadoId;
  dia: DataEmFormatoISO;
  precisa_recalcular: boolean;
  // usar enquanto está gerando jornada
  jornada?: {
    nome_da_jornada?: Texto;
    cor_da_jornada?: CorHex;
  }
  /**
   * O dia não tem jornada e deveria ter: o front pede a geração em
   * `gerar-jornadas-que-faltam`, que devolve o dia já calculado. O back só manda
   * o campo quando é `true`.
   */
  jornada_precisa_ser_gerada?: boolean;
}

export type DiaDaFolhaDados = DiaDaFolha | DiaSemDiaDaFolha;

export interface DiaDaFolhaResponse {
  sucesso: boolean;
  // dados: ReadonlyArray<DiaDaFolhaDados>;
  dados: DiaDaFolhaDados[];
}

// Helpers opcionais (type guards) para facilitar uso no frontend.
export const ehDiaDaFolhaCompleto = (dado: DiaDaFolhaDados): dado is DiaDaFolha =>
  "nome_empregado" in dado;
